import React, { useEffect, useState } from 'react';
import { toast } from '../../../../../../../../ui/toast';
import { Button } from '../../../../../../../../ui';
import { assessmentBEIQuestion } from '../../../../../../../../utils/api_service/Behavioral';
import { JobDescription } from '../../../../../../../../utils/types/JobOpenings';
import { generateBEIQuestions } from '../Constants';

interface Props {
    jobDescription?: JobDescription,
    selectedAttributes?: string[],
    shouldGenerate?: boolean,
    assessmentID: string,
    questions: any[],
    setQuestions: (value: ((prevState: any[]) => any[]) | any[]) => void,
    editingTimeLimit: number,
    setEditingTimeLimit: (value: (((prevState: number) => number) | number)) => void
}

export const BEIQuestionsContent = ({
  jobDescription,
  selectedAttributes,
  shouldGenerate,
  assessmentID,
  questions,
  setQuestions,
  editingTimeLimit,
  setEditingTimeLimit,
}: Props) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedQuestion, setEditedQuestion] = useState('');

  // Generate questions only if shouldGenerate is true and selectedAttributes exist
  useEffect(() => {
    if (shouldGenerate && selectedAttributes && selectedAttributes.length > 0) {
      setQuestions(generateBEIQuestions(selectedAttributes));
    }
  }, [shouldGenerate, selectedAttributes, setQuestions]);

  const handleEditClick = (questionItem: any) => {
    setEditingId(questionItem.id);
    setEditedQuestion(questionItem.question);
    setEditingTimeLimit(questionItem.timeLimit);
  };

  const handleSaveEdit = (questionId: number) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) =>
        q.id === questionId
          ? { ...q, question: editedQuestion, timeLimit: editingTimeLimit }
          : q,
      ),
    );
    setEditingId(null);
    setEditedQuestion('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedQuestion('');
    setEditingTimeLimit(editingTimeLimit);
  };

  const saveBEIQuestions = async () => {
    if (!questions || questions.length === 0) return;
    const payload = {
      assessmentId: assessmentID,
      questions: questions.map((q) => ({
        question: q.question,
        attribute: q.attribute,
      })),
      timeLimit: editingTimeLimit,
    };

    try {
      const response = await assessmentBEIQuestion(payload);
      if (response.ok) {
        toast.success('BEI questions saved successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Unable to complete the request at this time.', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Unable to complete the request at this time.', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <div className='flex-1 p-3 sm:p-6'>
      <div className='mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between'>
        <h2 className='font-medium text-primary-main text-lg sm:text-xl'>
          BEI Questions
        </h2>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6'>
          { questions.length > 0 && (
            <span className='text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full'>
              { questions.length } questions generated
            </span>
          ) }
          <Button
              variant='primary'
              label='Save Questions'
              onClick={saveBEIQuestions}
              disabled={
                !jobDescription?.behavioralAttributes ||
                            jobDescription.behavioralAttributes.length === 0 ||
                            questions.length === 0 ||
                            editingId !== null
              }
              className='w-full sm:w-auto'
          />
        </div>
      </div>

      { /* Global Time Limit Control */ }
      { questions.length > 0 && (
        <div className='bg-gradient-to-r from-primary-background/30 to-primary-background/10 border border-primary-main/20 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6'>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-center gap-3'>
              <div className='flex-shrink-0 w-10 h-10 bg-primary-main/10 rounded-full flex items-center justify-center'>
                <svg
                    className='w-5 h-5 text-primary-main'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                >
                  <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <div>
                <h3 className='text-sm font-semibold text-gray-800'>
                  Global Time Limit
                </h3>
                <p className='text-xs text-gray-600'>
                  Applies to all questions
                </p>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <label className='flex items-center gap-2'>
                <input
                    type='number'
                    value={editingTimeLimit}
                    onChange={(e) => setEditingTimeLimit(Number(e.target.value))}
                    disabled={editingId !== null}
                    className='w-24 border border-gray-300 rounded-md px-3 py-2 text-center font-medium focus:outline-none focus:ring-2 focus:ring-primary-main focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed'
                    min={30}
                    max={600}
                />
                <span className='text-sm font-medium text-gray-700'>seconds</span>
              </label>
            </div>
          </div>
        </div>
      ) }

      { questions.length > 0 ? (
        <div className='space-y-4'>
          { questions.map((questionItem) => (
            <div
                key={questionItem.id}
                className='bg-white border border-gray-200 rounded-lg p-3 sm:p-5 shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary-main/30'
            >
              <div className='flex items-start gap-3 sm:gap-4'>
                <div className='flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-primary-main text-white rounded-full flex items-center justify-center text-sm font-semibold'>
                  { questionItem.id }
                </div>
                <div className='flex-1'>
                  <div className='text-xs text-primary-main uppercase tracking-wide font-semibold mb-3 px-2 py-1 bg-primary-background/50 rounded-md inline-block'>
                    { questionItem.attribute }
                  </div>

                  { editingId === questionItem.id ? (
                    <div className='space-y-3'>
                      <textarea
                          value={editedQuestion}
                          onChange={(e) => setEditedQuestion(e.target.value)}
                          className='w-full text-gray-800 leading-relaxed text-base border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-primary-main focus:border-transparent'
                          rows={3}
                          autoFocus
                      />
                      <div className='flex gap-2'>
                        <button
                            onClick={() => handleSaveEdit(questionItem.id)}
                            className='px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-colors'
                        >
                          Save
                        </button>
                        <button
                            onClick={handleCancelEdit}
                            className='px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors'
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className='flex items-start justify-between gap-3 sm:gap-4'>
                      <div className='flex-1'>
                        <p className='text-sm text-gray-800 leading-relaxed sm:text-base'>
                          { questionItem.question }
                        </p>
                      </div>
                      <button
                          onClick={() => handleEditClick(questionItem)}
                          className='flex-shrink-0 p-2 text-gray-500 hover:text-primary-main hover:bg-gray-100 rounded-md transition-colors'
                          title='Edit question'
                      >
                        <svg
                            className='w-5 h-5'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                          />
                        </svg>
                      </button>
                    </div>
                  ) }
                </div>
              </div>
            </div>
          )) }
        </div>
      ) : (
        <div className='text-center py-16 text-gray-500'>
          <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
            <svg
                className='w-8 h-8 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          { !jobDescription?.behavioralAttributes ||
                    jobDescription.behavioralAttributes.length === 0 ? (
                      <>
                <p className='text-lg font-medium mb-2 text-gray-700'>
                          No behavioral attributes found
                </p>
                <p className='text-sm'>
                          Add behavioral attributes to the job description to generate BEI
                          questions.
                </p>
              </>
            ) : (
              <>
                <p className='text-lg font-medium mb-2 text-gray-700'>
                  Ready to generate BEI questions
                </p>
                <p className='text-sm'>
                  Select behavioral attributes from the sidebar and click
                  &quot;Generate Questions&quot; to create BEI questions.
                </p>
              </>
            ) }
        </div>
      ) }

      { /* Information Notice Box */ }
      <div className='mt-6 sm:mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-6'>
        <div className='flex items-start gap-3'>
          <div className='flex-shrink-0'>
            <svg
                className='w-6 h-6 text-blue-600 mt-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <div className='flex-1'>
            <h3 className='text-sm font-semibold text-blue-900 mb-2'>
              Adaptive BEI Follow-up Questions
            </h3>
            <div className='text-sm text-blue-800 space-y-2'>
              <p>
                Each main BEI question can generate up to{ ' ' }
                <strong>3 follow-up questions</strong>. These follow-up
                questions are automatically enabled when the candidate&apos;s
                response to the main question does not meet the minimum scoring
                threshold.
              </p>
              <p>
                The system analyzes the answer using the{ ' ' }
                <strong>STAR methodology</strong> and activates relevant
                follow-up questions to further probe areas where the response
                was weak or incomplete.
              </p>
              <p>
                This adaptive questioning approach ensures a comprehensive
                assessment of the candidate&apos;s behavioral competencies while
                maintaining interview efficiency.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
